import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, OnChanges, signal, effect } from '@angular/core';
import { AccessLevelType, AccessLevelTypeDescription, DocumentLegalValueCombination, DocumentLegalValueCombinationDescription } from '../../../../models/edocs.model';
import { FlowObjectDefinition } from '../../../../models/flow-object.model';
import { FORMBUILDER_OPTIONS_DEFAULT } from '../../../formio/formbuilder-options-default';
import { Formio, FormioUtils } from '@formio/angular';
import { Components, Templates } from '@formio/js';
import { EDocsService } from '../../../../services/edocs.service';
import { AuthService } from '../../../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../../shared/enums';
import { Utils } from '../../../../shared/utils';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TemplateRef } from '@angular/core';
import { FORMBUILDER_SCHEMA_DEFAULT } from '../../../formio/formbuilder-schema-default';
import { IBaseOption } from '../../../../models/base.model';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { FormBuilderTemplates } from '../../../formio/formbuilder-templates';
import { FlowDefinition, FlowTarget } from '../../../../models/flow.model';
import { IMPORTS } from './_imports';
import { NgxSpinnerService } from 'ngx-spinner';
import cloneDeep from 'lodash/cloneDeep';

@Component({
    selector: 'fod-form',
    templateUrl: './fod-form.component.html',
    styleUrl: './fod-form.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectDetailsFormComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('wrapperRef') wrapperRef: ElementRef;
    @ViewChild('formSchemaRef') formSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('importCsvRef') importCsvRef: TemplateRef<HTMLElement>;
    @ViewChild('formSchemaEditorRef') formSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    Utils = Utils;
    AccessLevelType = AccessLevelType;
    AccessLevelTypeDescription = AccessLevelTypeDescription;
    DocumentLegalValueCombination = DocumentLegalValueCombination;
    DocumentLegalValueCombinationDescription = DocumentLegalValueCombinationDescription;
    // #endregion

    // #region [props]
    options: any = FORMBUILDER_OPTIONS_DEFAULT;
    isFormioDayBugProcessed = false;
    formSchemaDialog: MatDialogRef<HTMLElement, any>;
    importCsvDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $formSchema = signal(FORMBUILDER_SCHEMA_DEFAULT as any);
    $formSchemaFlat = signal('');
    $selectedDropdown = signal(null as string);
    $availableDropdownsOptions = signal([] as IBaseOption[]);
    $csvFile = signal(null as any);
    $shouldShowFormBuilder = signal(true);
    // #endregion

    // #region [effects]
    $formSchemaSetup = effect(() => {
        if (Utils.isNotNullOrEmpty(this.$model()?.formSchema)) {
            this.$formSchema.set(JSON.parse(this.$model().formSchema));
        }
    }, { allowSignalWrites: true });
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private csvParser: NgxCsvParser,
        private spinner: NgxSpinnerService,
        private dialog: MatDialog,
        private toastr: ToastrService,
        private authService: AuthService,
        private edocsService: EDocsService
    ) { }

    async ngOnInit() {
        // injeção dos métodos para se obter as opções de dropdowns do E-Docs
        // em componentes do tipo "PdfUpload" / "PdfUploadMultiple" no FormBuilder]
        this.eDocsDropdownsOptionsInjection();

        // configuração do FormSchema
        this.formSchemaConfig();

        this.bindTableButtons();
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    moveComponentIntoTable(event, ctx: FlowObjectDetailsFormComponent) {
        let targetKey = event.currentTarget.parentElement.querySelector('select').value;
        let tableKey = event.currentTarget.dataset.tableKey;
        let targetComponent = ctx.$formSchema().components.find(x => x.key == targetKey);
        let tableComponent = ctx.$formSchema().components.find(x => x.key == tableKey);
        tableComponent.components = tableComponent.components || [];
        tableComponent.components.push(targetComponent);

        Utils.updSignal(ctx.$formSchema, x => {
            x.components = x.components.filter(y => y != targetComponent);
            return x;
        });

        this.recycleFormBuilder();
    }

    formBuilderChange(event) {
        switch (event.type) {
            case 'updateComponent':
                if (!this.isFormioDayBugProcessed) {
                    // contorno de bug do Formio
                    event.form?.components.filter(x => x.type == 'day').forEach(x => x.dayFirst = true);
                    this.isFormioDayBugProcessed = true;
                }
                break;

            case 'saveComponent':
                Utils.setSignal(this.$formSchema, event.form);
                Utils.updSignal(this.$model, x => {
                    x.formSchema = JSON.stringify(this.$formSchema());
                    return x;
                });

                this.processConditionalTags();
                break;

            case 'addComponent':
            case 'deleteComponent':
                Utils.setSignal(this.$formSchema, event.form);
                this.processConditionalTags();
                break;

            default:
                break;
        }
    }

    onSubmit() {
        if (this.$isReadOnlyMode()) return false;

        Utils.updSignal(this.$formSchema, x => {
            for (const component of x.components) {
                if (component.type === 'content' && component.html) {
                    component.html = Utils.removeBase64Images(component.html);
                }
            }
            return x;
        });

        Utils.updSignal(this.$model, x => {
            x.formSchema = JSON.stringify(this.$formSchema());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    showFormSchema() {
        this.$formSchemaFlat.set(JSON.stringify(this.$formSchema(), null, 4));

        let backupFormSchema = JSON.stringify(this.$formSchema());

        this.formSchemaDialog = this.dialog.open(this.formSchemaRef, {
            minWidth: '800px'
        });

        this.formSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.formSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.formSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.formSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.formSchemaDialog.afterClosed().subscribe(() => {
            let response = Utils.tryParseJson(this.formSchemaEditorRef.nativeElement.value);
            if (!response) {
                this.toastr.error(Enums.Messages.FormSchemaParsingError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                this.$formSchema.set(JSON.parse(backupFormSchema));
            } else {
                this.$formSchema.set(response);
            }

            setTimeout(() => this.processConditionalTags(), 2000);
        });
    }

    showImportCsv() {
        this.importCsvDialog = this.dialog.open(this.importCsvRef, {
            width: '800px',
            panelClass: 'import-csv-dialog'
        });

        this.importCsvDialog.afterOpened().subscribe(() => {
            for (let dropdown of this.$formSchema().components.filter(x => x.type == 'select')) {
                Utils.updSignal(this.$availableDropdownsOptions, x => {
                    x.push({
                        value: dropdown.key,
                        description: dropdown.label
                    });
                    return x;
                });
            }
        });

        this.importCsvDialog.afterClosed().subscribe(() => {
            Utils.updSignal(this.$model, x => {
                x.formSchema = JSON.stringify(this.$formSchema());
                return x;
            });
        });
    }

    loadOptions() {
        if (this.$csvFile().fileContent[0].length == 0) {
            this.toastr.warning(Enums.Messages.CsvEmptyError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        this.csvParser.parse(new File([new Uint8Array(this.$csvFile().fileContent[0])], 'default.csv'), {
            header: true,
            delimiter: ';'
        }).pipe().subscribe((result: any[]) => {
            if (result.length == 0) {
                this.toastr.warning(Enums.Messages.CsvNoRecordsError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                return;
            }

            result.forEach(x => Utils.updSignal(this.$formSchema, xx => {
                xx.components.find(y => y.key == this.$selectedDropdown()).data.values.push({
                    label: x.rotulo,
                    value: x.valor
                });
                return xx;
            }));

            this.toastr.success(Enums.Messages.CsvImportSuccess, Enums.Messages.Success);
        }, (error: NgxCSVParserError) => {
            this.toastr.error(Enums.Messages.CsvImportError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            console.error('CSVParser Error', error);
        });
    }

    startProcessingConditionalTags() {
        let i = 0;
        let interval = setInterval(() => {
            if (i == 5) {
                clearInterval(interval);
                return;
            }

            this.processConditionalTags();
            i++;
        }, 1500);
    }

    // #region [===== private methods =====]
    // #endregion

    private processConditionalTags() {
        document.querySelectorAll('.tag-conditional').forEach(x => x.remove());

        if (this.$formSchema()?.components == null) return;

        for (let item of this.$formSchema().components) {
            if (Utils.isNotNullOrEmpty(item.conditional?.show) || Utils.isNotNullOrEmpty(item.customConditional)) {
                let root = document.querySelector(`.formio-component-${item.key}`)?.parentElement;
                if (root == null || root.querySelector('.tag-conditional') != null) continue;

                let element = document.createElement('i');
                element.classList.add('fas');
                element.classList.add('fa-code-branch');
                element.classList.add('tag-conditional');

                if (Utils.isNotNullOrEmpty(item.customConditional)) {
                    element.classList.add('advanced');
                    element.setAttribute('title', Enums.Messages.ConditionalFieldAdvanced);
                } else {
                    element.setAttribute('title', Enums.Messages.ConditionalFieldSimple);
                }

                root.appendChild(element);
            }
        }
    }

    private bindTableButtons() {
        setInterval(() => {
            document.querySelectorAll('button.move-component').forEach(x => {
                x.removeAllListeners('click');
                x.addEventListener('click', event => this.moveComponentIntoTable(event, this));
            });
        }, 1000);
    }

    private recycleFormBuilder() {
        this.spinner.show('formbuilder');
        setTimeout(() => this.$shouldShowFormBuilder.set(false), 100);
        setTimeout(() => this.$shouldShowFormBuilder.set(true), 200);
        setTimeout(() => this.formSchemaConfig(), 200);
        setTimeout(() => this.eDocsDropdownsOptionsInjection(), 300);
        setTimeout(() => this.processConditionalTags(), 450);
        setTimeout(() => this.spinner.hide('formbuilder'), 700);
    }

    private eDocsDropdownsOptionsInjection() {
        let _this = this;

        let pdfUploadComponents = this.options.editForm.pdfUpload.find(x => x.key == 'display').components as any[];
        pdfUploadComponents = pdfUploadComponents.concat(this.options.editForm.pdfUploadMultiple.find(x => x.key == 'display').components as any[]);

        pdfUploadComponents.filter(x => x.key == 'documentLegalValueCombination').forEach(x => {
            x.data = {
                custom(context) {
                    let documentLegalValueCombinations = [];

                    for (let item in DocumentLegalValueCombination) {
                        if (!Number.isNaN(Number.parseInt(item))) {
                            documentLegalValueCombinations.push({
                                value: +item,
                                label: DocumentLegalValueCombinationDescription.get(+item)
                            });
                        }
                    }

                    return documentLegalValueCombinations;
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'accessLevel').forEach(x => {
            x.data = {
                custom(context) {
                    let accessLevels = [];

                    for (let item in AccessLevelType) {
                        if (!Number.isNaN(Number.parseInt(item)) && +item != AccessLevelType.Classified) {
                            accessLevels.push({
                                value: +item,
                                label: AccessLevelTypeDescription.get(+item)
                            });
                        }
                    }

                    return accessLevels;
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'activePlans').forEach(x => {
            x.customConditional = context => {
                return context.data.documentLegalValueCombination.value == DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoEletronicamente;
            };

            x.data = {
                custom(context) {
                    const getValues = async context => {
                        const patriarchId = _this._flowDefinition.patriarchId;
                        const response = await _this.edocsService.getPlanosAtivos(patriarchId);

                        if (!response.isSuccess) {
                            _this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        let activePlans = response.data.sort((a, b) => {
                            let first = a.codigo || a.nome || '';
                            let second = b.codigo || b.nome || '';
                            return first.localeCompare(second);
                        });

                        return activePlans.map(x => ({
                            value: x.id,
                            label: `${x.codigo} - ${x.nome.toUpperCase()}`
                        }));
                    };

                    getValues(context).then(response => {
                        context.instance.setItems(response, true);
                    });
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'documentClass').forEach(x => {
            x.customConditional = context => {
                return context.data.documentLegalValueCombination.value == DocumentLegalValueCombination.ServidorDocumentoEletronicoAssinadoEletronicamente
                    && JSON.stringify(context.data.activePlans) != JSON.stringify({});
            };

            x.data = {
                custom(context) {
                    const getValues = async context => {
                        const response = await _this.edocsService.getClassesAtivas(context.data.activePlans.value);

                        if (!response.isSuccess) {
                            _this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        // filtrando apenas as classes e não seus agrupadores
                        let documentClasses = response.data.filter(x =>
                            // XX.XX.XX.XX
                            x.codigo.split('.').length == 4
                            // XX.XXXX
                            || x.codigo.split('.').slice(-1)[0].length == 4
                        ).sort((a, b) => `${a.codigo.trim()} - ${a.nome.trim()}`.localeCompare(`${b.codigo.trim()} - ${b.nome.trim()}`));

                        return documentClasses.map(x => ({
                            value: x.id,
                            label: `${x.codigo.trim()} - ${x.nome.trim()}`
                        }));
                    };

                    getValues(context).then(response => {
                        context.instance.setItems(response, true);
                    });
                }
            }
        });

        pdfUploadComponents.filter(x => x.key == 'legalReasoning').forEach(x => {
            x.customConditional = context => {
                return context.data.accessLevel.value == AccessLevelType.Confidential;
            };

            x.data = {
                custom(context) {
                    const getValues = async context => {
                        let response = null;
                        if (_this._flowDefinition.targetId == FlowTarget.Citizen) {
                            response = await _this.edocsService.getFundamentosLegaisCidadao();
                        } else {
                            const patriarchId = _this._flowDefinition.organizationId;
                            response = await _this.edocsService.getFundamentosLegaisPatriarca(patriarchId);
                        }

                        if (!response.isSuccess) {
                            _this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        let legalReasonings = response.data.sort((a, b) => a.ordenacao - b.ordenacao).filter(x => x.nivelAcesso.toUpperCase() == "SIGILOSO");

                        return legalReasonings.map(x => ({
                            value: x.id,
                            label: `${x.nivelAcesso.trim()}: ${x.nome.trim()}`
                        }));
                    };

                    getValues(context).then(response => {
                        context.instance.setItems(response, true);
                    });
                }
            }
        });
    }

    private formSchemaConfig() {
        const textfieldComponent = Components.components.textfield;
        const textfieldSchema = textfieldComponent.schema;
        Components.components.textfield.schema = (...extend) => {
            return textfieldSchema({ label: null }, ...extend);
        };

        const textareaComponent = Components.components.textarea;
        const textareaSchema = textareaComponent.schema;
        Components.components.textarea.schema = (...extend) => {
            return textareaSchema({ label: null }, ...extend);
        };

        const textfieldEditForm = textfieldComponent.editForm;
        Components.components.textfield.editForm = (...args) => {
            let editForm = textfieldEditForm.apply(this, args);
            let newForm = cloneDeep(editForm);

            let htmlElement = newForm.components
                .find(x => x.key == 'tabs')?.components?.find(x => x.key == 'data')?.components?.find(x => x.key == 'calculateValuePanel')?.components?.find(x => x.type == 'htmlelement');

            if (htmlElement != null && !htmlElement.content.includes('_$_accpf')) {
                let div = document.createElement('div');
                div.innerHTML = htmlElement.content;
                div.querySelector('table').insertAdjacentHTML(
                    'beforeend',
                    '<tr><th>_$_accpf</th><td>CPF do usuário solicitante obtido do Acesso Cidadão.</td></tr>'
                );
                htmlElement.content = div.innerHTML;
            }

            return newForm;
        };

        class Cpf extends textfieldComponent {
            static schema(...extend) {
                return textfieldComponent.schema({
                    type: 'cpf',
                    label: null,
                    key: 'cpf',
                    inputMask: '999.999.999-99',
                    validate: {
                        custom: "function isCpfValid(cpf) { cpf = cpf.replace(/[^\\d]+/g,''); if (cpf == '') return false; if (cpf.length != 11 || cpf == '00000000000' || cpf == '11111111111' || cpf == '22222222222' || cpf == '33333333333' || cpf == '44444444444' || cpf == '55555555555' || cpf == '66666666666' || cpf == '77777777777' || cpf == '88888888888' || cpf == '99999999999' ) return false; add = 0; for (i=0; i < 9; i ++) add += Number.parseInt(cpf.charAt(i)) * (10 - i); rev = 11 - (add % 11); if (rev == 10 || rev == 11) rev = 0; if (rev != Number.parseInt(cpf.charAt(9))) return false; add = 0; for (i = 0; i < 10; i ++) add += Number.parseInt(cpf.charAt(i)) * (11 - i); rev = 11 - (add % 11); if (rev == 10 || rev == 11) rev = 0; if (rev != Number.parseInt(cpf.charAt(10))) return false; return true; }\r\nvalid = !component.validate?.required && (input == null || input == '') ? true : isCpfValid(input) ? true : 'CPF inválido';"
                    }
                });
            }

            static get builderInfo() {
                return {
                    title: 'CPF',
                    icon: 'fas fa-id-card',
                    group: 'advanced',
                    weight: 21,
                    schema: Cpf.schema()
                };
            }
        }

        class Cnpj extends textfieldComponent {
            static schema(...extend) {
                return textfieldComponent.schema({
                    type: 'cnpj',
                    label: null,
                    key: 'cnpj',
                    inputMask: '**.***.***/****-99',
                    validate: {
                        custom: "function isCnpjValid(cnpj) { let tamanhoCNPJSemDV = 12; let regexCNPJSemDV = /^([A-Z0-9]){12}$/; let regexCNPJ = /^([A-Z0-9]){12}([0-9]){2}$/; let cnpjZerado = '00000000000000'; let pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]; cnpj = cnpj.replace(/[^0-9a-zA-Z]/g, '').toUpperCase(); if (cnpj.length !== 14 || [...cnpj].every(c => c === cnpj[0])) return false; const calculaDV = (cnpj) => { if (regexCNPJSemDV.test(cnpj) && cnpj !== cnpjZerado.substring(0, tamanhoCNPJSemDV)) { let somatorioDV1 = 0; let somatorioDV2 = 0; for (let i = 0; i < tamanhoCNPJSemDV; i++) { const asciiDigito = cnpj.charCodeAt(i) - 48; somatorioDV1 += asciiDigito * pesosDV[i + 1]; somatorioDV2 += asciiDigito * pesosDV[i]; } const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11); somatorioDV2 += dv1 * pesosDV[tamanhoCNPJSemDV]; const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11); return `${dv1}${dv2}`; } return false; }; if (regexCNPJ.test(cnpj) && cnpj !== cnpjZerado) { const dvInformado = cnpj.substring(tamanhoCNPJSemDV); const dvCalculado = calculaDV(cnpj.substring(0, tamanhoCNPJSemDV)); return dvInformado === dvCalculado; } return false; }\r\nvalid = !component.validate?.required && (input == null || input == '') ? true : isCnpjValid(input) ? true : 'CNPJ inválido';"
                    }
                });
            }

            static get builderInfo() {
                return {
                    title: 'CNPJ',
                    icon: 'fas fa-suitcase',
                    group: 'advanced',
                    weight: 22,
                    schema: Cnpj.schema()
                };
            }
        }

        class GeoJson extends textareaComponent {
            static schema(...extend) {
                return textareaComponent.schema({
                    type: 'geoJson',
                    label: null,
                    key: 'geoJson',
                    validate: {
                        custom: "if (!component.validate?.required && (input == null || input == '')) { valid = true; } else { let response = window.geojsonhint.hint(input, { precisionWarning: false, ignoreRightHandRule: true }); if (response.length == 0) { valid = true; } else { valid = 'GeoJSON inválido'; for (let error of response) { valid += ` — [Linha: ${error.line}; Erro: ${error.message}] `; } } }"
                    }
                });
            }

            static get builderInfo() {
                return {
                    title: 'GeoJSON',
                    icon: 'fas fa-draw-polygon',
                    group: 'advanced',
                    weight: 23,
                    schema: GeoJson.schema()
                };
            }
        }

        class EDocsProtocol extends textfieldComponent {
            static schema(...extend) {
                return textfieldComponent.schema({
                    type: 'eDocsProtocol',
                    label: null,
                    key: 'eDocsProtocol',
                    case: 'uppercase',
                    inputMask: '9999-******'
                });
            }

            static get builderInfo() {
                return {
                    title: 'Protocolo E-Docs',
                    icon: 'file-export',
                    group: 'basic',
                    weight: -10,
                    schema: EDocsProtocol.schema()
                };
            }
        }

        Formio.use({
            components: {
                cpf: Cpf,
                cnpj: Cnpj,
                geoJson: GeoJson,
                eDocsProtocol: EDocsProtocol
            }
        });

        const numberSchema = Components.components.number.schema;
        Components.components.number.schema = (...extend) => {
            return numberSchema({ label: null }, ...extend);
        };

        const passwordSchema = Components.components.password.schema;
        Components.components.password.schema = (...extend) => {
            return passwordSchema({ label: null }, ...extend);
        };

        const checkboxSchema = Components.components.checkbox.schema;
        Components.components.checkbox.schema = (...extend) => {
            return checkboxSchema({ label: null }, ...extend);
        };

        const selectboxesSchema = Components.components.selectboxes.schema;
        Components.components.selectboxes.schema = (...extend) => {
            return selectboxesSchema({ label: null }, ...extend);
        };

        const selectComponent = Components.components.select;
        const selectSchema = selectComponent.schema;
        Components.components.select.schema = (...extend) => {
            return selectSchema({ label: null }, ...extend);
        };

        class SelectRepository extends selectComponent {
            static schema(...extend) {
                return selectComponent.schema({
                    type: 'selectRepository',
                    label: null,
                    key: 'selectRepository',
                    hidden: true,
                    hideLabel: true,
                    dataSrc: 'json',
                    data: {
                        json: [
                            { label: 'Título 1', value: 'valor1', propriedadeDeComparacao: 'valorDeComparacao1' },
                            { label: 'Título 2', value: 'valor2', propriedadeDeComparacao: 'valorDeComparacao2' }
                        ]
                    },
                    valueProperty: 'value'
                });
            }

            static get builderInfo() {
                return {
                    title: 'Lista Repositório',
                    icon: 'th-list',
                    group: 'advanced',
                    weight: 70,
                    schema: SelectRepository.schema()
                };
            }
        }

        class SelectFiltered extends selectComponent {
            static schema(...extend) {
                return selectComponent.schema({
                    type: 'selectFiltered',
                    label: null,
                    key: 'selectFiltered',
                    dataSrc: 'custom',
                    data: {
                        custom: "values = form.components.find(x => x.key == 'listaRepositorio').data.json.filter(x => x.propriedadeDeComparacao.toLowerCase() == data['campoDeComparacao'].toLowerCase());"
                    }
                });
            }

            static get builderInfo() {
                return {
                    title: 'Lista Filtrada',
                    icon: 'th-list',
                    group: 'advanced',
                    weight: 71,
                    schema: SelectFiltered.schema()
                };
            }
        }

        Formio.use({
            components: {
                selectRepository: SelectRepository,
                selectFiltered: SelectFiltered
            }
        });

        const radioSchema = Components.components.radio.schema;
        Components.components.radio.schema = (...extend) => {
            return radioSchema({ label: null }, ...extend);
        };

        const buttonSchema = Components.components.button.schema;
        Components.components.button.schema = (...extend) => {
            return buttonSchema({ label: null }, ...extend);
        };

        const emailSchema = Components.components.email.schema;
        Components.components.email.schema = (...extend) => {
            return emailSchema({ label: null }, ...extend);
        };

        const urlSchema = Components.components.url.schema;
        Components.components.url.schema = (...extend) => {
            return urlSchema({ label: null }, ...extend);
        };

        const phoneNumberComponent = Components.components.phoneNumber;
        const phoneNumberSchema = phoneNumberComponent.schema;
        Components.components.phoneNumber.schema = (...extend) => {
            let override = {
                label: null,
                inputMask: null,
                allowMultipleMasks: true,
                inputMasks: [
                    {
                        label: 'Celular',
                        mask: '(99) 99999-9999'
                    },
                    {
                        label: 'Fixo',
                        mask: '(99) 9999-9999'
                    }
                ]
            };
            return phoneNumberSchema(override, ...extend);
        };

        class LandlineNumber extends phoneNumberComponent {
            static schema(...extend) {
                return phoneNumberComponent.schema({
                    type: 'landlineNumber',
                    label: null,
                    key: 'landlineNumber',
                    inputMask: '(99) 9999-9999',
                    validate: null,
                    allowMultipleMasks: false,
                    inputMasks: null
                });
            }

            static get builderInfo() {
                return {
                    title: 'Telefone Fixo',
                    icon: 'fas fa-phone-alt',
                    group: 'advanced',
                    weight: 31,
                    schema: LandlineNumber.schema()
                };
            }
        }

        class CellNumber extends phoneNumberComponent {
            static schema(...extend) {
                return phoneNumberComponent.schema({
                    type: 'cellNumber',
                    label: null,
                    key: 'cellNumber',
                    inputMask: '(99) 99999-9999',
                    validate: null,
                    allowMultipleMasks: false,
                    inputMasks: null
                });
            }

            static get builderInfo() {
                return {
                    title: 'Telefone Celular',
                    icon: 'fas fa-mobile-alt',
                    group: 'advanced',
                    weight: 32,
                    schema: CellNumber.schema()
                };
            }
        }
        Formio.use({
            components: {
                landlineNumber: LandlineNumber,
                cellNumber: CellNumber
            }
        });

        const daySchema = Components.components.day.schema;
        Components.components.day.schema = (...extend) => {
            let override = {
                //label: FORMBUILDER_OPTIONS.i18n.pt[daySchema(...extend).label],
                label: null,
                hideInputLabels: true,
                useLocaleSettings: true,
                dayFirst: true,
                fields: {
                    day: {
                        placeholder: 'Dia'
                    },
                    month: {
                        placeholder: 'Mês'
                    },
                    year: {
                        placeholder: 'Ano'
                    }
                }
            };
            return daySchema(override, ...extend);
        };

        const timeSchema = Components.components.time.schema;
        Components.components.time.schema = (...extend) => {
            return timeSchema({ label: null }, ...extend);
        };

        const currencySchema = Components.components.currency.schema;
        Components.components.currency.schema = (...extend) => {
            return currencySchema({ label: null }, ...extend);
        };

        const contentSchema = Components.components.content.schema;
        Components.components.content.schema = (...extend) => {
            return contentSchema({ label: null }, ...extend);
        };

        const tableComponent = Components.components.table;
        class TableCustom extends tableComponent {
            static schema(...extend) {
                return tableComponent.schema({
                    type: 'tableCustom',
                    label: null,
                    key: 'tableCustom'
                });
            }

            static get builderInfo() {
                return {
                    title: 'Tabela',
                    icon: 'table',
                    group: 'advanced',
                    weight: 100,
                    schema: TableCustom.schema()
                };
            }
        }

        const datagridComponent = Components.components.datagrid;
        class DatagridCustom extends datagridComponent {
            static schema(...extend) {
                return datagridComponent.schema({
                    type: 'datagridCustom',
                    label: null,
                    key: 'datagridCustom'
                });
            }

            static get builderInfo() {
                return {
                    title: 'Tabela de Dados',
                    icon: 'th',
                    group: 'advanced',
                    weight: 101,
                    schema: DatagridCustom.schema()
                };
            }
        }

        Formio.use({
            components: {
                tableCustom: TableCustom,
                datagridCustom: DatagridCustom
            }
        });

        const builderSidebarForm = Templates.current.builderSidebar.form;
        Templates.current.builderSidebar.form = ctx => FormBuilderTemplates.builderSidebarFormTemplate(builderSidebarForm, ctx);

        const builderComponentForm = Templates.current.builderComponent.form;
        Templates.current.builderComponent.form = ctx => FormBuilderTemplates.builderComponentFormTemplate(builderComponentForm, ctx);

        const builderEditFormForm = Templates.current.builderEditForm.form;
        Templates.current.builderEditForm.form = ctx => FormBuilderTemplates.builderEditFormTemplate(builderEditFormForm, ctx);

        const datagridForm = Templates.current.datagrid.form;
        Templates.current.datagrid.form = ctx => FormBuilderTemplates.datagridFormTemplate(datagridForm, ctx);

        const currencyEditForm = Components.components.currency.editForm;
        Components.components.currency.editForm = (...args: any[]) => {
            let editForm = currencyEditForm.apply(this, args);
            let currency: any = FormioUtils.getComponent(editForm.components, 'currency', false);
            currency.defaultValue = 'BRL';
            currency.data.values = [
                { label: 'Real Brasileiro (BRL)', value: 'BRL' },
                { label: 'Dólar Americano (USD)', value: 'USD' },
                { label: 'Euro (EUR)', value: 'EUR' },
                { label: 'Libra Esterlina (GBP)', value: 'GBP' }
            ];

            return editForm;
        };
    }
}
